/**
 * @description
 * ## 事件中心
 * @example
 *
 * 支持 事件名 + 类名 监听
 * 一个事件可以加多个监听器
 *
 * import EventEmitter from "butter-event-emitter"
 *
 * let eventEmitter = new EventEmitter();
 *
 * //监听
 * eventEmitter.on('event', function() {...});
 * eventEmitter.on('event.myEvent', function() {...}); //以 .myEvent 为类名
 * eventEmitter.on('event event1.myEvent1 event2', function() {...});
 * eventEmitter.on({
 *   'event': function() {...},
 *   ...
 * });
 *
 * //触发
 * eventEmitter.emit('event'); //触发所有以 "event" 为事件名的事件
 * eventEmitter.emit('.myEvent'); //触发所有以 ".myEvent" 为类名的事件
 * eventEmitter.emit('event.myEvent'); //只触发以 "event" 为事件名，以 ".myEvent" 以类名的事件
 *
 * //解绑
 * eventEmitter.off('event') //解绑所有以 "event" 为事件名的事件
 * eventEmitter.off('.myEvent'); //解绑所有以 ".myEvent" 为类名的事件
 * eventEmitter.off('event.myEvent'); //只解绑以 "event" 为事件名，以 ".myEvent" 以类名的事件
 * eventEmitter.off('event .class event.class'); //一次性解绑多个事件
 * eventEmitter.off('.*-class'); //解绑所有以 -class 结尾的类名的事件
 *
 * Note: 如果在处理函数中返回 false，则会终止该事件后续的处理流程 (只会终止同一事件，不会终止同一类名)
 * @module butter-event-emitter
 * */

import {
  isNumber,
  isFunction,
  isString,
  isObject,
  isArray,
  Debug
} from "buttercam/utils";

const debug = Debug('EventEmitter');

/**
 * 事件管理
 * */
class EventEmitter {
  
  /**
   * 创建一个 eventEmitter
   * @param {object} [config={}] - 配置
   * @param {number|null} [config.maxEvents=null] - 最大事件数量 (默认无限)
   * @param {number|null} [config.maxHandlers=null] - 最大处理器数量 (默认无限)
   * */
  constructor(config = {}) {
    
    /**
     * 事件集合
     * @type {Object.<string, array>}
     * */
    this.events = {};
    
    /**
     * 最大事件数量
     * @type {number}
     * */
    this.maxEvents = config.maxEvents || null;
  
    /**
     * 最大处理器数量
     * @type {number}
     * */
    this.maxHandlers = config.maxHandlers || null;
  }
  
  /**
   * 事件数量
   * @type {number}
   * */
  get countOfEvents() {
    return Object.keys(this.events).length
  }
  
  /**
   * 处理器数量
   * @type {number}
   * */
  get countOfHandlers() {
    return Object.keys(this.events).reduce((accumulator, symbol) => {
      return accumulator += this.events[symbol].length
    }, 0)
  }
  
  /**
   * 监听数量是否大于等于规定数量
   * @private
   * @returns {boolean}
   * */
  _ListenerExceeded() {
    let eventsExceeded = !!this.maxEvents && this.countOfEvents >= this.maxEvents;
    let handlersExceeded = !!this.maxHandlers && this.countOfHandlers >= this.maxHandlers;
    return eventsExceeded || handlersExceeded
  }
  
  /**
   * 监听
   * @private
   * @param {String} symbol - 事件名称
   * @param {Function} handler - 事件处理函数
   * @param {Number} [order] - 本次监听的处理顺序，默认为第一个
   * @returns {EventEmitter}
   * */
  _listen(symbol, handler, order) {
    let {events} = this;
    let hasOrder = isNumber(order) && order >= 0;
    let eventName = EventEmitter.getEventName(symbol);
    let className = EventEmitter.getClassName(symbol);
    if(!isFunction(handler)) {
      return this
    }
    if(this._ListenerExceeded()) {
      debug.warn('listeners exceeded!');
      return this
    }
    if(!events[eventName]) {
      events[eventName] = [];
    }
    if(hasOrder) {
      events[eventName].splice(order, 0, {className, handler})
    } else {
      events[eventName].push({className, handler})
    }
    return this
  }
  
  /**
   * @private
   * 参数为对象的监听
   * @param {Object.<string, function>} listeners - 监听列表
   * @returns {EventEmitter}
   * */
  _listenWithObject(listeners) {
    Object.keys(listeners).forEach(symbol => {
      this._listen(symbol, listeners[symbol])
    });
    return this
  }
  
  /**
   * 参数为字符串的监听
   * @private
   * @param {String} symbols - 事件名称
   * @param {Function} handler - 处理函数
   * @param {Number} [order] - 本次监听的处理顺序
   * @returns {EventEmitter}
   * */
  _listenWithKeys(symbols, handler, order) {
    let _symbols = symbols.split(' ');
    _symbols.forEach(symbol => {
      this._listen(symbol, handler, order)
    });
    return this
  }
  
  /**
   * 监听事件
   * @param {Object.<string, function>|String} symbols - 可以是字符串或对象
   * @param {Function} [handler] - 处理函数 (如果 symbols 是对象则该参数没有)
   * @param {Number} [order] - 处理顺序 (如果 symbols 是对象则该参数没有)
   * @returns {EventEmitter}
   * */
  on(symbols, handler, order) {
    if(!symbols) {
      return this
    }
    if(isString(symbols)) {
      return this._listenWithKeys(symbols, handler, order)
    }
    if(isObject(symbols)) {
      return this._listenWithObject(symbols)
    }
    return this
  }
  
  /**
   * 解绑事件
   * @private
   * @param {String} eventName - 事件名
   * @param {String} [className] - 类名
   * @returns {EventEmitter}
   * */
  _off(eventName, className) {
    if(!className) {
      Object.keys(this.events).forEach(key => {
        if(EventEmitter.match(eventName, key)) {
          delete this.events[eventName]
        }
      });
      return this
    }
    let eventsCollection = this.events[eventName];
    if(isArray(eventsCollection)) {
      this.events[eventName] = eventsCollection.filter((eventCollection) => {
        return !EventEmitter.match(className, eventCollection.className)
      });
    }
    return this
  }
  
  /**
   * 根据类名解绑事件
   * @private
   * @param {String} className - 类名
   * @returns {EventEmitter}
   * */
  _offWithClassName(className) {
    Object.keys(this.events).forEach(eventName => {
      this._off(eventName, className)
    });
    return this
  }
  
  /**
   * 解绑所有事件
   * @private
   * @returns {EventEmitter}
   * */
  _offAll() {
    this.events = {};
    return this
  }
  
  /**
   * 解绑事件
   * @param {String} [symbols] - 需解绑的事件
   * @return {EventEmitter}
   * */
  off(symbols) {
    if(!symbols) {
      return this._offAll()
    }
    let _symbols = symbols.split(' ');
    _symbols.forEach(_symbol => {
      let eventName = EventEmitter.getEventName(_symbol);
      let className = EventEmitter.getClassName(_symbol);
      if(!eventName && !className) {
        return this
      }
      if(!eventName) {
        return this._offWithClassName(className)
      }
      this._off(eventName, className)
    });
    return this
  }
  
  /**
   * 触发事件
   * @private
   * @param {string} eventName - 要触发的事件名
   * @param {string} [className] - 要触发的类名
   * @param {*} args - 监听器参数 (此处是 ...args，可以为多个参数)
   * @returns {EventEmitter}
   * */
  _emit(eventName, className, ...args) {
    let eventsCollections = this.events[eventName];
    let executable = className ?
      (_className) => _className === className :
      () => true;
    if(!eventsCollections) {
      return this
    }
    for(let eventsCollection of eventsCollections) {
      let result;
      if(!executable(eventsCollection.className)) {
        continue
      }
      result = eventsCollection.handler(...args);
      if(result === false) {
        return this
      }
    }
    return this
  }
  
  /**
   * 以类名触发事件
   * @private
   * @param {string} className - 以 "." 开头的类名
   * @param {*} args - 监听器参数 (此处是 ...args，可以为多个参数)
   * @returns {EventEmitter}
   * */
  _emitWithClassName(className, ...args) {
    Object.keys(this.events).forEach(eventName => {
      this._emit(eventName, className, ...args)
    });
    return this
  }
  
  /**
   * 触发事件
   * @param {string} symbol - 要触发的事件名称
   * @param {*} args - 监听器参数 (此处是 ...args，可以为多个参数)
   * @returns {EventEmitter}
   * */
  emit(symbol, ...args) {
    let className = EventEmitter.getClassName(symbol);
    let eventName = EventEmitter.getEventName(symbol);
    if(!className && !eventName) {
      return this
    }
    if(!eventName) {
      return this._emitWithClassName(className, ...args)
    }
    return this._emit(eventName, className, ...args)
  }
  
  /**
   * 获取事件名
   * @param {String} symbol - 事件标记
   * @returns {String}
   * */
  static getEventName(symbol) {
    try {
      return symbol.match(/^[^.]+/)[0]
    } catch (e) {
      return ""
    }
  }
  
  /**
   * 获取类名
   * @param {String} symbol - 事件标记
   * @returns {String}
   * */
  static getClassName(symbol) {
    try {
      return symbol.match(/\..+$/)[0]
    } catch (e) {
      return ""
    }
  }
  
  /**
   * 匹配字符串
   * @param {string} reference - 匹配规则
   * @param {string} string - 待匹配的字符串
   * @return {boolean}
   * @example
   * EventEmitter.match('.*-upload', '.blabla-upload') // => true
   * */
  static match(reference, string) {
    if(/\*/.test(reference)) {
      reference = reference
        .replace(/\./, '\\.')
        .replace(/\*/, '[\\s\\S]*');
      return new RegExp(`^${reference}$`).test(string)
    }
    return reference === string
  }
  
}

/**
 * default export
 * @type module:butter-event-emitter~EventEmitter
 * */
export default EventEmitter